import { ApiPlatformEnum, EcommerceCartDataSourceEnum } from '@inter-ikea-kompis/enums';
import { ExceptionTypeEnum, ServiceException } from '@inter-ikea-kompis/exceptions';
import { GlobalCacheStore } from '@inter-ikea-kompis/utilities';
import ChinaAuthenticationService from '../../services/authentication/china/ChinaAuthenticationService.js';
import ChinaCartService from '../../services/shopping/china/cart/ChinaCartService.js';
import IksaShoppingBagService from '../../services/shopping/iksa/IksaShoppingBagService.js';
import IngkaCartService from '../../services/shopping/ingka/cart/IngkaCartService.js';
import IowsShoppingBagService from '../../services/shopping/iows/IowsShoppingBagService.js';
import MvEcomShoppingBagService from '../../services/shopping/mvecom/MvEcomShoppingBagService.js';
import NifCartService from '../../services/shopping/nif/NifCartService.js';
import DexfConfiguration from '../../utilities/DexfConfiguration.js';
import IrwPlatformCommunication from '../../utilities/platform-communication/IrwPlatformCommunication.js';
import RoigPlatformCommunication from '../../utilities/platform-communication/RoigPlatformCommunication.js';
import AuthenticationQueue from './AuthenticationQueue.js';
/**
 * This class is used as a simple interface for shopping services.
 */
export default class ShoppingCartService {
    /**
     * Constructor.
     *
     * @param options Options.
     */
    constructor(options) {
        this.consumer = options.consumer;
        this.contract = options.contract;
        this.applicationName = options.applicationName;
        this.locale = options.locale;
        this.cache = options.cache || new GlobalCacheStore();
        this.dexfConfiguration = new DexfConfiguration(options);
    }
    /**
     * Adds items to shopping cart.
     *
     * @param options Options.
     * @param options.items Array of shopping items.
     * @param options.designCode VPC code used to group articles as a planner purchase.
     * @param options.addDesignCodeAsGroup If true the articles will be added as a group in the markets where this is possible. This requires designCode to be set.
     * @param options.singleItems Single items to be added individually outside the group.
     * @returns Promise object containing ICart.
     */
    async addToCart(options) {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const eCommerceCartDataSource = kompis.serviceSettings.eCommerceCartDataSource;
        const apiPlatform = kompis.serviceSettings.apiPlatform;
        switch (eCommerceCartDataSource) {
            case EcommerceCartDataSourceEnum.cartApi:
                return await this.addToCartOneWeb({
                    items: options.items,
                    designCode: options.designCode,
                    addDesignCodeAsGroup: options.addDesignCodeAsGroup,
                    singleItems: options.singleItems
                });
            case EcommerceCartDataSourceEnum.chinaCartApi:
                return await this.addToCartChina({
                    items: options.items,
                    designCode: options.designCode
                });
            case EcommerceCartDataSourceEnum.iows:
                return await this.addToCartIows({
                    items: options.items
                });
            case EcommerceCartDataSourceEnum.mvecom:
                return await this.addToCartMvEcom({
                    items: options.items
                });
            case EcommerceCartDataSourceEnum.mvecomV2:
                return await this.addToCartMvEcom({
                    items: options.items,
                    useBearerToken: true
                });
            case EcommerceCartDataSourceEnum.nifApi:
                return await this.addToCartNif({
                    items: options.items
                });
            case EcommerceCartDataSourceEnum.iksa:
                return await this.addToCartIksa({
                    items: options.items
                });
            case EcommerceCartDataSourceEnum.default:
                switch (apiPlatform) {
                    case ApiPlatformEnum.irw:
                        return await this.addToCartIows({
                            items: options.items
                        });
                    case ApiPlatformEnum.roig:
                        return await this.addToCartNif({
                            items: options.items
                        });
                    default:
                        throw new ServiceException(`Failed to add items to cart. Unknown api platform "${apiPlatform}".`);
                }
            default:
                throw new ServiceException(`Failed to add items to cart. Unknown cart data source "${eCommerceCartDataSource}".`);
        }
    }
    /**
     * Returns total amount of items in a shopping cart.
     *
     * @returns Promise object.
     */
    async getCartCount() {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const eCommerceCartDataSource = kompis.serviceSettings.eCommerceCartDataSource;
        const apiPlatform = kompis.serviceSettings.apiPlatform;
        switch (eCommerceCartDataSource) {
            case EcommerceCartDataSourceEnum.cartApi:
                return await this.getCartCountOneWeb();
            case EcommerceCartDataSourceEnum.chinaCartApi:
                return await this.getCartCountChina();
            case EcommerceCartDataSourceEnum.iows:
                return await this.getCartCountIows();
            case EcommerceCartDataSourceEnum.mvecom:
                return await this.getCartCountMvEcom();
            case EcommerceCartDataSourceEnum.mvecomV2:
                return await this.getCartCountMvEcom({ useBearerToken: true });
            case EcommerceCartDataSourceEnum.nifApi:
                return await this.getCartCountNif();
            case EcommerceCartDataSourceEnum.iksa:
                return await this.getCartCountIksa();
            case EcommerceCartDataSourceEnum.default:
                switch (apiPlatform) {
                    case ApiPlatformEnum.irw:
                        return await this.getCartCountIows();
                    default:
                        throw new ServiceException(`Failed to get cart count. Unknown api platform "${apiPlatform}".`);
                }
            default:
                throw new ServiceException(`Failed to get cart count. Unknown cart data source "${eCommerceCartDataSource}".`);
        }
    }
    /**
     * Adds items to shopping cart.
     *
     * @param options Options.
     * @param options.items Array of shopping items.
     * @returns Promise object.
     */
    async addToCartIows(options) {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const shoppingService = new IowsShoppingBagService({
            consumer: this.consumer,
            contract: this.contract,
            endpoint: kompis.endpoints.iowsShoppingBagService
        });
        const userId = await this.getIowsUserId();
        const cart = await shoppingService.addToCart({
            items: options.items,
            userId
        });
        const platform = new IrwPlatformCommunication({ applicationName: this.applicationName });
        platform.setCartCount(cart.cartCount);
        return cart;
    }
    /**
     * Returns cart count for IOWS.
     *
     * @returns Promise object.
     */
    async getCartCountIows() {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const shoppingService = new IowsShoppingBagService({
            consumer: this.consumer,
            contract: this.contract,
            endpoint: kompis.endpoints.iowsShoppingBagService
        });
        const userId = await this.getIowsUserId();
        return await shoppingService.getCartCount({
            userId
        });
    }
    /**
     * Adds items to shopping cart.
     *
     * @param options Options.
     * @param options.items Array of shopping items.
     * @param options.useBearerToken If bearer token should be used for user authentication.
     * @returns Promise object.
     */
    async addToCartMvEcom(options) {
        const serviceOptions = await this.getMvEcomService(options.useBearerToken);
        const cart = await serviceOptions.service.addToCart({
            items: options.items,
            userId: serviceOptions.userId
        });
        const platform = new IrwPlatformCommunication({ applicationName: this.applicationName });
        platform.setCartCount(cart.cartCount);
        return cart;
    }
    /**
     * Returns cart count for MvEcom.
     *
     * @returns Promise object.
     */
    async getCartCountMvEcom(options) {
        const serviceOptions = await this.getMvEcomService(options?.useBearerToken);
        return await serviceOptions.service.getCartCount({
            userId: serviceOptions.userId
        });
    }
    /**
     * Returns authenticated MvEcom service and user id.
     *
     * @param useBearerToken If bearer token should be used for user authentication.
     * @returns Promise object.
     */
    async getMvEcomService(useBearerToken) {
        const { kompis } = await this.dexfConfiguration.getSettings();
        let bearerToken;
        let userId;
        if (useBearerToken) {
            const token = await this.getMvEcomToken(kompis.endpoints.mvEcomAuthenticationService);
            if (token) {
                bearerToken = token.value;
                userId = token.customerId;
            }
            else {
                userId = await this.getIowsUserId();
            }
        }
        else {
            userId = await this.getIowsUserId();
        }
        const shoppingService = new MvEcomShoppingBagService({
            mvEcomApiKey: kompis.serviceSettings.mvEcomApiKey,
            endpoint: kompis.endpoints.mvEcomShoppingBagService,
            bearerToken
        });
        return { service: shoppingService, userId };
    }
    /**
     * Adds items to shopping cart.
     *
     * @param options Options.
     * @param options.items Array of shopping items.
     * @param options.designCode Design code (VPC-code) to group articles in checkout and assembly services.
     * @param options.addDesignCodeAsGroup Boolean to group the articles or not in cart.
     * @param options.singleItems Single items to be added individually outside the group.
     * @returns Promise object.
     */
    async addToCartOneWeb(options) {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const shoppingService = new IngkaCartService({
            endpoint: kompis.endpoints.ingkaCartServiceV2,
            itemGroupingInBagEnabled: kompis.serviceSettings.itemGroupingInBagEnabled,
            consumerName: kompis.serviceSettings.oneWebShoppingCartConsumerName
        });
        const authQueue = new AuthenticationQueue(this.cache);
        const token = await authQueue.getAuthTokenOneWeb({
            endpoint: kompis.endpoints.oneWebAuthenticationToken
        });
        if (!token) {
            throw new ServiceException('Token is not valid.');
        }
        const cart = await shoppingService.addToCart({
            items: options.items,
            token,
            designCode: options.designCode,
            addDesignCodeAsGroup: options.addDesignCodeAsGroup,
            singleItems: options.singleItems
        });
        const platform = new IrwPlatformCommunication({ applicationName: this.applicationName });
        platform.setCartCount(cart.cartCount);
        return cart;
    }
    /**
     * Returns cart count for one web.
     *
     * @returns Promise object.
     */
    async getCartCountOneWeb() {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const shoppingService = new IngkaCartService({
            endpoint: kompis.endpoints.ingkaCartServiceV2,
            itemGroupingInBagEnabled: kompis.serviceSettings.itemGroupingInBagEnabled,
            consumerName: kompis.serviceSettings.oneWebShoppingCartConsumerName
        });
        const authQueue = new AuthenticationQueue(this.cache);
        const token = await authQueue.getAuthTokenOneWeb({
            endpoint: kompis.endpoints.oneWebAuthenticationToken
        });
        if (!token) {
            throw new ServiceException('Token is not valid.');
        }
        return await shoppingService.getCartCount({
            token
        });
    }
    /**
     * Adds items to shopping cart.
     *
     * @param options Options.
     * @param options.items Array of shopping items.
     * @param options.designCode VPC/design code to group articles in checkout.
     * @returns Promise object.
     */
    async addToCartChina(options) {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const shoppingService = new ChinaCartService({
            endpoint: kompis.endpoints.chinaCartService
        });
        const authQueue = new AuthenticationQueue(this.cache);
        const token = await authQueue.getAuthTokenChina({
            refreshTokenEndpoint: kompis.endpoints.chinaAuthenticationRefreshToken,
            loginPageEndpoint: kompis.endpoints.chinaAuthenticationLoginPage
        });
        if (!token) {
            throw new ServiceException('Token is not valid.');
        }
        try {
            return await shoppingService.addToCart({
                items: options.items,
                token,
                designCode: options.designCode
            });
        }
        catch (error) {
            if (error.exceptionType === ExceptionTypeEnum.serverErrorResponse &&
                error.serverResponse.status === 401) {
                const token = await authQueue.getAuthTokenChina({
                    refreshTokenEndpoint: kompis.endpoints.chinaAuthenticationRefreshToken,
                    loginPageEndpoint: kompis.endpoints.chinaAuthenticationLoginPage,
                    login: true
                });
                if (!token) {
                    throw new ServiceException('Token is not valid.');
                }
                return await shoppingService.addToCart({
                    items: options.items,
                    token,
                    designCode: options.designCode
                });
            }
            throw error;
        }
    }
    /**
     * Returns cart count for china.
     *
     * @returns Promise object.
     */
    async getCartCountChina() {
        const { kompis } = await this.dexfConfiguration.getSettings();
        if (!new ChinaAuthenticationService({
            refreshTokenEndpoint: kompis.endpoints.chinaAuthenticationRefreshToken,
            loginPageEndpoint: kompis.endpoints.chinaAuthenticationLoginPage
        }).hasCookie()) {
            return 0;
        }
        const shoppingService = new ChinaCartService({
            endpoint: kompis.endpoints.chinaCartService
        });
        const authQueue = new AuthenticationQueue(this.cache);
        const token = await authQueue.getAuthTokenChina({
            refreshTokenEndpoint: kompis.endpoints.chinaAuthenticationRefreshToken,
            loginPageEndpoint: kompis.endpoints.chinaAuthenticationLoginPage
        });
        if (!token) {
            throw new ServiceException('Token is not valid.');
        }
        try {
            return await shoppingService.getCartCount({
                token
            });
        }
        catch (error) {
            if (error.exceptionType === ExceptionTypeEnum.serverErrorResponse &&
                error.serverResponse.status === 401) {
                const token = await authQueue.getAuthTokenChina({
                    refreshTokenEndpoint: kompis.endpoints.chinaAuthenticationRefreshToken,
                    loginPageEndpoint: kompis.endpoints.chinaAuthenticationLoginPage,
                    login: true
                });
                if (!token) {
                    throw new ServiceException('Token is not valid.');
                }
                return await shoppingService.getCartCount({
                    token
                });
            }
            throw error;
        }
    }
    /**
     * Adds items to shopping cart.
     *
     * @param options Options.
     * @param options.items Array of shopping items.
     * @returns Promise object.
     */
    async addToCartNif(options) {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const shoppingService = new NifCartService({
            endpoint: kompis.endpoints.nifECommerceApiEndpoint,
            frameContextEndpoint: kompis.endpoints.bitboxCookieConsentService,
            locale: this.locale,
            nifCartUseBearerToken: kompis.serviceSettings.nifCartUseBearerToken,
            cache: this.cache
        });
        const platform = new RoigPlatformCommunication();
        if (!kompis.serviceSettings.nifCartUseBearerToken) {
            const authQueue = new AuthenticationQueue(this.cache);
            await authQueue.authenticateNif({
                endpoint: kompis.endpoints.nifECommerceApiEndpoint
            });
        }
        const cart = await shoppingService.addToCart({
            items: options.items
        });
        if (cart.cartCount) {
            platform.setCartCount(cart.cartCount);
        }
        else {
            console.warn('No cartCount returned from shoppingService.addToCart.');
        }
        return cart;
    }
    /**
     * Returns cart count for NIF.
     *
     * @returns Promise object.
     */
    async getCartCountNif() {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const shoppingService = new NifCartService({
            endpoint: kompis.endpoints.nifECommerceApiEndpoint,
            frameContextEndpoint: kompis.endpoints.bitboxCookieConsentService,
            locale: this.locale,
            nifCartUseBearerToken: kompis.serviceSettings.nifCartUseBearerToken,
            cache: this.cache
        });
        if (!kompis.serviceSettings.nifCartUseBearerToken) {
            const authQueue = new AuthenticationQueue(this.cache);
            await authQueue.authenticateNif({
                endpoint: kompis.endpoints.nifECommerceApiEndpoint
            });
        }
        return await shoppingService.getCartCount();
    }
    /**
     * Adds items to shopping cart.
     *
     * @param options Options.
     * @param options.items Array of shopping items.
     * @returns Promise object.
     */
    async addToCartIksa(options) {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const shoppingService = new IksaShoppingBagService({
            consumer: this.consumer,
            contract: this.contract,
            endpoint: kompis.endpoints.iksaShoppingBagService
        });
        const userId = await this.getIowsUserId();
        const cart = await shoppingService.addToCart({
            items: options.items,
            userId
        });
        const platform = new IrwPlatformCommunication({ applicationName: this.applicationName });
        platform.setCartCount(cart.cartCount);
        return cart;
    }
    /**
     * Returns cart count for IKSA.
     *
     * @returns Promise object.
     */
    async getCartCountIksa() {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const shoppingService = new IksaShoppingBagService({
            consumer: this.consumer,
            contract: this.contract,
            endpoint: kompis.endpoints.iksaShoppingBagService
        });
        const userId = await this.getIowsUserId();
        return await shoppingService.getCartCount({
            userId
        });
    }
    /**
     * Returns authenticated IOWS user id.
     *
     * @returns Promise of user id as string.
     */
    async getIowsUserId() {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const authQueue = new AuthenticationQueue(this.cache);
        const user = await authQueue.getAuthenticationIows({
            consumer: this.consumer,
            contract: this.contract,
            endpoint: kompis.endpoints.iowsAuthenticationServiceV2
        });
        return user.id;
    }
    async getMvEcomToken(endpoint) {
        const authQueue = new AuthenticationQueue(this.cache);
        return await authQueue.getAuthTokenMvEcom({
            endpoint: endpoint,
            locale: this.locale
        });
    }
}
//# sourceMappingURL=ShoppingCartService.js.map